import React, { useContext, useState, useRef, useEffect, useMemo } from 'react';

import { ExpandMoreButton } from '@components/ExpandMoreButton';
import MUICheckbox from '@material-ui/core/Checkbox';
import MUICollapse from '@material-ui/core/Collapse';
import MUIList from '@material-ui/core/List';
import MUIListItem from '@material-ui/core/ListItem';
import MUIListItemText from '@material-ui/core/ListItemText';
import MUIPaper from '@material-ui/core/Paper';
import MUIPopover from '@material-ui/core/Popover';
import MUITable from '@material-ui/core/Table';
import MUITableBody from '@material-ui/core/TableBody';
import MUITableCell from '@material-ui/core/TableCell';
import MUITableContainer from '@material-ui/core/TableContainer';
import MUITableHead from '@material-ui/core/TableHead';
import MUITableRow from '@material-ui/core/TableRow';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { sum } from 'lodash-es';

export interface TableProps {
  columnFixedUntil?: number;
  fixedWidths?: number[];
  className?: string;
  size?: 'small' | 'medium';
}

export interface TableStillCellProps {
  type?: 'selectable' | 'collapsable' | 'checkable';
  label: React.ReactNode;
}

export interface PrivateTableCellProps {
  disabled?: boolean;
  sticky: boolean;
  columnIndex: number;
  isLastOfFixedColumn: boolean;
}

export interface TableSelectableCellProps extends TableStillCellProps {
  type: 'selectable';
  options: { value: string | number | boolean | undefined | null; label: string }[];
  onChange: (value: string | number | boolean | undefined | null) => any;
}

export interface TableCollapsableCellProps extends TableStillCellProps {
  type: 'collapsable';
  open: boolean;
  onClick: () => any;
}

export interface TableCheckableCellProps extends TableStillCellProps {
  type: 'checkable';
  checked: boolean;
  onChange: (value: boolean) => any;
}

export type TableCellPropsUnion =
  | TableStillCellProps
  | TableSelectableCellProps
  | TableCollapsableCellProps
  | TableCheckableCellProps;

export interface TableRowProps {
  className?: string;
  columns: TableCellPropsUnion[];
  disabled?: boolean;
}

const orderingChoicesStrings = createStrings({
  ja: {
    desc: '時間順 新→古',
    asc: '時間順 古→新',
    dictDesc: '辞書順 降順',
    dictAsc: '辞書順 昇順',
  },
});

const tableCellGuards = {
  isSelectable(cell: TableCellPropsUnion): cell is TableSelectableCellProps {
    return cell.type === 'selectable';
  },
  isCollapsable(cell: TableCellPropsUnion): cell is TableCollapsableCellProps {
    return cell.type === 'collapsable';
  },
  isCheckable(cell: TableCellPropsUnion): cell is TableCheckableCellProps {
    return cell.type === 'checkable';
  },
};

const TableContext = React.createContext({
  columnFixedUntil: -1,
  scrollLeft: 0,
  fixedWidths: [] as number[],
});

const TableRowContext = React.createContext({
  head: false,
});

const useStyles = makeMuiStyles((theme, common) => ({
  table: {
    ...common.middleText,
    position: 'relative',
    border: 8,
  },
  headCell: {
    borderBottom: 'none',
    color: theme.palette.grey[700],
    whiteSpace: 'pre',
    background: theme.palette.neutral.content,
    textAlign: 'left',
    paddingTop: 12,
    paddingBottom: 12,
    paddingLeft: 6,
    paddingRight: 6,
  },
  '$headCell $sticky': {
    background: theme.palette.neutral.content,
  },
  bodyCell: {
    borderBottom: 'none',
    color: theme.palette.grey[800],
    whiteSpace: 'pre-line',
    background: theme.palette.background.paper,
    wordWrap: 'break-word',
    padding: 6,
  },
  '$bodyCell $sticky': {
    background: theme.palette.background.paper,
  },
  collapsableBodyCell: {
    borderBottom: 'none',
    background: theme.palette.background.default,
  },
  cellText: {
    minHeight: 18,
    display: 'inline-block',
  },
  sticky: {
    position: 'sticky',
    overflow: 'hidden',
    padding: 0,
  },
  stickyInner: {
    padding: 12,
    '&:after': {
      content: '""',
      position: 'absolute',
      zIndex: -1,
      top: 0,
      right: 5,
      paddingRight: 5,
      width: '200%',
      height: '999px',
      transition: '0.1s',
      transform: 'translateY(-50%)',
      boxShadow: '0 0 5px transparent',
    },
    [theme.breakpoints.down('xs')]: {
      padding: 8,
    },
  },
  lastStickyInner: {
    position: 'relative',
    '&:after': {
      boxShadow: `0 0 5px ${theme.palette.grey[500]}`,
    },
  },
  rowButton: {
    ...common.flexInline,
    alignItems: 'center',
  },
  expandMoreButtonWrapper: {
    display: 'flex',
  },
  optionList: {
    minWidth: 120,
  },
  link: {
    color: theme.palette.primary.main,
  },
  checkbox: {
    margin: -9,
  },
  disabledRow: {
    '& td > div > div': {
      pointerEvents: 'none',
      opacity: 0.3,
    },
  },
}));

const TableCellContainer: React.FC<React.PropsWithChildren<PrivateTableCellProps>> = (props) => {
  const { sticky, columnIndex, isLastOfFixedColumn, children } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { head } = useContext(TableRowContext);
  const { columnFixedUntil, fixedWidths } = useContext(TableContext);

  if (columnFixedUntil !== fixedWidths.length) {
    throw new Error(
      `The table has fixed width for column 0 to ${columnFixedUntil}, but get fixedWidths.length === ${fixedWidths.length}. Expected to be equal. `,
    );
  }

  const leftOffset =
    columnIndex < columnFixedUntil ? sum(fixedWidths.slice(0, columnIndex)) : undefined;

  return (
    <MUITableCell
      classes={{
        root: clsx(sticky && classes.sticky, head ? classes.headCell : classes.bodyCell),
      }}
      style={{
        zIndex: 999 - columnIndex - (head ? 0 : 1),
        ...(leftOffset != null && { left: leftOffset, width: fixedWidths[columnIndex] }),
      }}
    >
      <div
        className={clsx(
          sticky && classes.stickyInner,
          isLastOfFixedColumn && classes.lastStickyInner,
        )}
        style={leftOffset != null ? { width: fixedWidths[columnIndex] } : undefined}
      >
        <div>{children}</div>
      </div>
    </MUITableCell>
  );
};

const TableStillCell: React.FC<TableStillCellProps & PrivateTableCellProps> = React.memo(
  (props) => {
    const { label } = props;

    return <TableCellContainer {...props}>{label}</TableCellContainer>;
  },
);

const TableSelectableCell: React.FC<TableSelectableCellProps & PrivateTableCellProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { label, options, onChange } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const open = anchorEl !== null;

  return (
    <TableCellContainer {...props}>
      <button className={classes.rowButton} onClick={handleClick}>
        <span>{label}</span>
        <span className={classes.expandMoreButtonWrapper}>
          <ExpandMoreButton expanded={open} />
        </span>
      </button>
      <MUIPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MUIList classes={{ root: classes.optionList }}>
          {options.map((option) => {
            return (
              <MUIListItem
                key={option.label}
                button
                onClick={() => {
                  onChange(option.value);
                  setAnchorEl(null);
                }}
              >
                <MUIListItemText classes={{ primary: classes.middleTextImportant }}>
                  {option.label}
                </MUIListItemText>
              </MUIListItem>
            );
          })}
        </MUIList>
      </MUIPopover>
    </TableCellContainer>
  );
};

const TableCollapsableCell: React.FC<TableCollapsableCellProps & PrivateTableCellProps> = (
  props,
) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { label, open, onClick } = props;

  return (
    <TableCellContainer {...props}>
      <button className={classes.rowButton} onClick={onClick}>
        <span>{label}</span>
        <span className={classes.expandMoreButtonWrapper}>
          <ExpandMoreButton expanded={open} />
        </span>
      </button>
    </TableCellContainer>
  );
};

const TableCheckableCell: React.FC<TableCheckableCellProps & PrivateTableCellProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { checked, onChange } = props;

  return (
    <TableCellContainer {...props}>
      <MUICheckbox
        checked={checked}
        onChange={() => {
          onChange(!checked);
        }}
        color="primary"
        classes={{ root: classes.checkbox }}
      />
    </TableCellContainer>
  );
};

const TableRow: React.FC<React.PropsWithChildren<TableRowProps>> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { columns, className, children, disabled } = props;
  const collapsableOrUndefined = useMemo(() => {
    return columns.find((column) => tableCellGuards.isCollapsable(column));
  }, [columns]);
  const hasCollapsable = collapsableOrUndefined !== undefined;
  const { columnFixedUntil = -1 } = useContext(TableContext);

  return (
    <>
      <MUITableRow className={clsx(className, disabled && classes.disabledRow)}>
        {columns.map((column, index) => {
          const isSticky = index < columnFixedUntil;
          const isLastOfFixedColumn = index + 1 === columnFixedUntil;

          if (tableCellGuards.isSelectable(column)) {
            return (
              <TableSelectableCell
                {...column}
                sticky={isSticky}
                columnIndex={index}
                isLastOfFixedColumn={isLastOfFixedColumn}
                key={index}
              />
            );
          }
          if (tableCellGuards.isCollapsable(column)) {
            return (
              <TableCollapsableCell
                {...column}
                sticky={isSticky}
                columnIndex={index}
                isLastOfFixedColumn={isLastOfFixedColumn}
                key={index}
              />
            );
          }
          if (tableCellGuards.isCheckable(column)) {
            return (
              <TableCheckableCell
                {...column}
                sticky={isSticky}
                columnIndex={index}
                isLastOfFixedColumn={isLastOfFixedColumn}
                key={index}
              />
            );
          }
          return (
            <TableStillCell
              {...column}
              sticky={isSticky}
              columnIndex={index}
              isLastOfFixedColumn={isLastOfFixedColumn}
              key={index}
            />
          );
        })}
      </MUITableRow>
      {hasCollapsable ? (
        <MUITableRow>
          <MUITableCell
            colSpan={columns.length}
            classes={{ root: classes.collapsableBodyCell }}
            style={{ paddingBottom: 0, paddingTop: 0 }}
          >
            <MUICollapse
              in={(collapsableOrUndefined as TableCollapsableCellProps).open}
              timeout="auto"
              unmountOnExit
            >
              {children}
            </MUICollapse>
          </MUITableCell>
        </MUITableRow>
      ) : null}
    </>
  );
};

const TableHead: React.FC<React.PropsWithChildren<{}>> = (props) => {
  const { children } = props;

  return (
    <TableRowContext.Provider value={{ head: true }}>
      <MUITableHead>{children}</MUITableHead>
    </TableRowContext.Provider>
  );
};

const TableBody: React.FC<React.PropsWithChildren<{}>> = (props) => {
  const { children } = props;

  return (
    <TableRowContext.Provider value={{ head: false }}>
      <MUITableBody>{children}</MUITableBody>
    </TableRowContext.Provider>
  );
};

interface Aggregate {
  Head: typeof TableHead;
  Body: typeof TableBody;
  Row: typeof TableRow;
}

type TableComponent = React.ForwardRefExoticComponent<
  React.PropsWithChildren<TableProps> & React.RefAttributes<HTMLTableElement>
> &
  Aggregate;

export const Table: TableComponent = React.forwardRef(function TableComponent(props, forwardRef) {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { columnFixedUntil = 0, fixedWidths = [], className, size, children } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  useEffect(() => {
    if (ref.current === null) {
      return () => {};
    }

    const handler = (_event: Event) => {
      if (ref.current === null) {
        return;
      }

      setScrollLeft(ref.current.scrollLeft);
    };
    ref.current.addEventListener('scroll', handler);

    const cleanup = () => {
      if (ref.current !== null) {
        ref.current.removeEventListener('scroll', handler);
      }
    };
    return cleanup;
  }, []);

  return (
    <TableContext.Provider value={{ columnFixedUntil, scrollLeft, fixedWidths }}>
      <MUITableContainer
        ref={ref}
        component={MUIPaper}
        style={{ boxShadow: 'none' }}
        className={className}
      >
        <MUITable size={size} ref={forwardRef} classes={{ root: classes.table }} stickyHeader>
          {children}
        </MUITable>
      </MUITableContainer>
    </TableContext.Provider>
  );
}) as TableComponent;

Table.Head = TableHead;
Table.Body = TableBody;
Table.Row = TableRow;

export interface CellFactory {
  create(label: React.ReactNode): TableCellPropsUnion;
  createBlank(): TableCellPropsUnion;
  createOrdering(
    label: React.ReactNode,
    onChange: TableSelectableCellProps['onChange'],
  ): TableCellPropsUnion;
  createDictOrdering(
    label: React.ReactNode,
    onChange: TableSelectableCellProps['onChange'],
  ): TableCellPropsUnion;
  createSelectable(
    label: React.ReactNode,
    options: TableSelectableCellProps['options'],
    onChange: TableSelectableCellProps['onChange'],
  ): TableCellPropsUnion;
  createCheckable(
    checked: TableCheckableCellProps['checked'],
    onChange: TableCheckableCellProps['onChange'],
  ): TableCellPropsUnion;
}

export const ORDERING = Object.freeze({
  DESC: 'DESC',
  ASC: 'ASC',
} as const);

export const cellFactory: CellFactory = Object.freeze({
  create: (label: any) => {
    return {
      label,
    };
  },
  createBlank: () => {
    return {
      label: <></>,
    };
  },
  createOrdering: (label: any, onChange: any) => {
    return {
      label,
      type: 'selectable' as const,
      options: [
        {
          label: orderingChoicesStrings.desc,
          value: ORDERING.DESC,
        },
        {
          label: orderingChoicesStrings.asc,
          value: ORDERING.ASC,
        },
      ],
      onChange,
    };
  },
  createDictOrdering: (label: any, onChange: any) => {
    return {
      label,
      type: 'selectable' as const,
      options: [
        {
          label: orderingChoicesStrings.dictDesc,
          value: ORDERING.DESC,
        },
        {
          label: orderingChoicesStrings.dictAsc,
          value: ORDERING.ASC,
        },
      ],
      onChange,
    };
  },
  createSelectable: (label: any, options: any, onChange: any) => {
    return {
      label,
      type: 'selectable' as const,
      options,
      onChange,
    };
  },
  createCheckable: (checked: any, onChange: any) => {
    return {
      label: <></>,
      type: 'checkable' as const,
      checked,
      onChange,
    };
  },
});
